import { useState, useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { Artwork } from '../data/types';

const Card = styled.div<{ variant?: 'default' | 'paper' }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
  align-items: start;
  padding: 0.75rem;
  background: ${props => props.variant === 'paper' 
    ? 'rgba(247, 243, 235, 0.98)' 
    : 'rgba(255, 255, 255, 0.05)'};
  border: ${props => props.variant === 'paper' 
    ? '1px solid rgba(109, 107, 73, 0.2)' 
    : '1px solid rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  box-shadow: ${props => props.variant === 'paper' 
    ? '0 2px 12px rgba(109, 107, 73, 0.1)' 
    : 'none'};
  transition: transform 0.3s ease, background-color 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-4px);
    ${props => props.variant === 'paper' 
      ? 'box-shadow: 0 8px 24px rgba(109, 107, 73, 0.15);' 
      : 'background: rgba(255, 255, 255, 0.08);'}
  }

  @media (min-width: 640px) {
    gap: 1.5rem;
    padding: 1rem;
  }

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
    gap: 4rem;
    padding: 2rem;
    box-shadow: ${props => props.variant === 'paper' 
      ? '0 4px 20px rgba(109, 107, 73, 0.12)' 
      : 'none'};
  }
`;

const ImageContainer = styled.div<{ variant?: 'default' | 'paper' }>`
  position: relative;
  padding: 0;
  cursor: pointer;
  overflow: hidden;
  border-radius: 4px;
  transition: transform 0.35s ease, box-shadow 0.35s ease;

  &:hover {
    transform: scale(1.02);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0);
    transition: background 0.25s ease;
    pointer-events: none;
  }

  &:hover::after {
    background: rgba(0, 0, 0, 0.08);
  }

  @media (min-width: 768px) {
    padding: 0.5rem;
  }
`;

const Image = styled.img`
  transition: transform 0.4s ease;
  ${ImageContainer}:hover & {
    transform: scale(1.03);
  }
  width: 100%;
  /* Mobile-first */
  height: 240px;
  object-fit: contain;
  display: block;

  @media (min-width: 480px) {
    height: 280px;
  }

  @media (min-width: 640px) {
    height: 320px;
  }

  @media (min-width: 768px) {
    height: 600px;
  }
`;

const Info = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    gap: 2rem;
  }
`;

const Title = styled.h4<{ variant?: 'default' | 'paper' }>`
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
  line-height: 1.3;
  letter-spacing: 0.02em;
  color: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
  text-transform: uppercase;
  font-family: ${props => props.variant === 'paper' ? 'Georgia, "Times New Roman", serif' : 'inherit'};

  @media (min-width: 640px) {
    font-size: 1.25rem;
  }

  @media (min-width: 768px) {
    font-size: 1.75rem;
    margin-bottom: 0.75rem;
  }
`;

const Details = styled.p<{ variant?: 'default' | 'paper' }>`
  font-size: 0.85rem;
  color: ${props => props.variant === 'paper' ? '#4a4540' : 'rgba(255, 255, 255, 0.8)'};
  margin: 0;
  line-height: 1.5;

  @media (min-width: 640px) {
    font-size: 0.9rem;
  }

  @media (min-width: 768px) {
    font-size: 1rem;
    line-height: 1.6;
  }
`;

const SizeInfo = styled(Details)<{ variant?: 'default' | 'paper' }>`
  color: ${props => props.variant === 'paper' ? '#6D6B49' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 0.8rem;
  margin-top: 0.25rem;

  @media (min-width: 768px) {
    font-size: 0.9rem;
    margin-top: 0.5rem;
  }
`;

const Price = styled.div<{ variant?: 'default' | 'paper' }>`
  margin-top: 1rem;
  padding: 0.75rem;
  background: ${props => props.variant === 'paper' 
    ? 'rgba(244, 199, 70, 0.15)' 
    : 'rgba(255, 255, 255, 0.08)'};
  text-align: center;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    background: ${props => props.variant === 'paper' 
      ? 'rgba(244, 199, 70, 0.2)' 
      : 'rgba(255, 255, 255, 0.12)'};
  }

  @media (min-width: 640px) {
    margin-top: 1.25rem;
    padding: 1rem;
  }

  @media (min-width: 768px) {
    margin-top: 2.5rem;
    padding: 1.75rem;
  }
`;

const PriceText = styled.p<{ variant?: 'default' | 'paper' }>`
  font-size: 1rem;
  font-weight: 500;
  color: ${props => props.variant === 'paper' ? '#2c2a26' : '#fff'};
  margin: 0;
  letter-spacing: 0.02em;

  @media (min-width: 640px) {
    font-size: 1.1rem;
  }

  @media (min-width: 768px) {
    font-size: 1.3rem;
  }
`;

const LightboxOverlay = styled.div<{ $visible: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.92);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  cursor: pointer;
  padding: 1rem;
  box-sizing: border-box;
  opacity: ${props => props.$visible ? 1 : 0};
  transition: opacity 0.28s ease-out;
`;

const LightboxContent = styled.div<{ $visible: boolean }>`
  max-width: 95vw;
  max-height: 95vh;
  cursor: default;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
  transform: scale(${props => props.$visible ? 1 : 0.94});
  opacity: ${props => props.$visible ? 1 : 0};
  transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.28s ease-out;
`;

const LightboxImage = styled.img`
  max-width: 100%;
  max-height: 85vh;
  object-fit: contain;
  border-radius: 4px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.4);
`;

const LightboxInfo = styled.div`
  color: #fff;
  text-align: center;
  font-size: 0.9rem;
  opacity: 0.9;
`;

const LightboxCloseButton = styled.button`
  position: fixed;
  top: max(1rem, env(safe-area-inset-top));
  right: 1rem;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.25);
  color: white;
  font-size: 24px;
  line-height: 1;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, transform 0.2s ease, border-color 0.2s ease;
  z-index: 1001;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
    border-color: rgba(255, 255, 255, 0.4);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.98);
  }
`;

interface ArtworkCardProps {
  artwork: Artwork;
  variant?: 'default' | 'paper';
}

const LIGHTBOX_ANIM_MS = 280;

export const ArtworkCard = ({ artwork, variant = 'default' }: ArtworkCardProps) => {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isLightboxVisible, setIsLightboxVisible] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasMedium = artwork.medium && artwork.medium.trim() !== '';
  const hasSize = artwork.size && artwork.size.trim() !== '';

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    if (isLightboxOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isLightboxOpen]);

  useEffect(() => {
    if (isLightboxOpen) {
      const t = requestAnimationFrame(() => {
        requestAnimationFrame(() => setIsLightboxVisible(true));
      });
      return () => cancelAnimationFrame(t);
    }
    setIsLightboxVisible(false);
  }, [isLightboxOpen]);

  const requestClose = () => {
    setIsLightboxVisible(false);
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    closeTimeoutRef.current = window.setTimeout(() => {
      closeTimeoutRef.current = null;
      setIsLightboxOpen(false);
    }, LIGHTBOX_ANIM_MS);
  };

  const handleImageClick = () => {
    if (artwork.image) setIsLightboxOpen(true);
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) requestClose();
  };

  return (
    <Card variant={variant}>
      {artwork.image && (
        <ImageContainer
          variant={variant}
          onClick={handleImageClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => e.key === 'Enter' && handleImageClick()}
          aria-label={`View ${artwork.title} in full size`}
        >
          <Image src={artwork.image} alt={artwork.title} loading="lazy" decoding="async" />
        </ImageContainer>
      )}
      <Info>
        <Title variant={variant}>
          {artwork.title}
          {artwork.titleThai && artwork.titleThai.trim() !== '' && ` (${artwork.titleThai})`}
        </Title>
        {hasMedium && (
          <Details variant={variant}>Medium: {artwork.medium}</Details>
        )}
        {hasSize && (
          <SizeInfo variant={variant}>Size: {artwork.size}</SizeInfo>
        )}
        <Price variant={variant}>
          <PriceText variant={variant}>{artwork.price}</PriceText>
        </Price>
      </Info>

      {isLightboxOpen && artwork.image && (
        <LightboxOverlay $visible={isLightboxVisible} onClick={handleOverlayClick}>
          <LightboxCloseButton
            onClick={() => requestClose()}
            aria-label="Close"
          >
            ×
          </LightboxCloseButton>
          <LightboxContent $visible={isLightboxVisible} onClick={(e) => e.stopPropagation()}>
            <LightboxImage src={artwork.image} alt={artwork.title} loading="eager" decoding="async" />
            <LightboxInfo>
              <div><strong>{artwork.title}</strong></div>
              {hasMedium && <div>{artwork.medium}</div>}
              {hasSize && <div>{artwork.size}</div>}
              <div>{artwork.price}</div>
            </LightboxInfo>
          </LightboxContent>
        </LightboxOverlay>
      )}
    </Card>
  );
}; 